import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';

import { configureApiClient } from '@/api/client';

import {
  apiBaseUrl,
  apiScope,
  devBypassEnabled,
  loginRequest,
  msalConfig,
  msalConfigured,
} from './config';

/**
 * Sign-in, then wire the API client (spec §9.2: MSAL → configureApiClient → fetchMe → render).
 *
 * The order matters: nothing may issue a request before a token provider exists, so the app is not
 * rendered until this resolves. Token acquisition is silent-first and falls back to a redirect —
 * a popup blocked by policy would leave the app in a state the user cannot get out of.
 */

export interface AuthSession {
  mode: 'msal' | 'dev-bypass';
  account: { name: string | null; username: string | null } | null;
  signOut: () => Promise<void>;
}

export async function bootstrapAuth(): Promise<AuthSession> {
  if (devBypassEnabled || !msalConfigured) {
    configureApiClient({ baseUrl: apiBaseUrl, devBypass: true });
    return {
      mode: 'dev-bypass',
      account: { name: 'Local Dev User', username: 'dev@greco.local' },
      signOut: async () => {},
    };
  }

  const msal = new PublicClientApplication(msalConfig);
  await msal.initialize();

  const redirectResult = await msal.handleRedirectPromise();
  const account = redirectResult?.account ?? msal.getAllAccounts()[0] ?? null;
  if (!account) {
    await msal.loginRedirect(loginRequest);
    // loginRedirect navigates away; this promise never settles in practice.
    return new Promise<AuthSession>(() => {});
  }
  msal.setActiveAccount(account);

  configureApiClient({
    baseUrl: apiBaseUrl,
    getAccessToken: async () => {
      try {
        const result = await msal.acquireTokenSilent({ scopes: [apiScope], account });
        return result.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await msal.acquireTokenRedirect({ scopes: [apiScope], account });
          return null;
        }
        throw error;
      }
    },
  });

  return {
    mode: 'msal',
    account: { name: account.name ?? null, username: account.username ?? null },
    signOut: async () => {
      await msal.logoutRedirect({ account });
    },
  };
}
